import { useSyncExternalStore } from 'react'
import { useAuth } from './auth'

/**
 * De tijdelijke ontgrendeling van geblurde bedragen.
 *
 * De opgeslagen voorkeur staat op het account (zie /api/me); wat hier woont is de
 * vraag "mag het nu even zichtbaar zijn". Die leeft in sessionStorage en niet in
 * React-state, om twee redenen: een refresh mag je ontgrendeling niet kosten, maar
 * een nieuw tabblad of een gesloten browser hoort weer dicht te beginnen.
 *
 * Het venster schuift mee met je activiteit. Werk je door, dan blijft het open;
 * loop je weg van je scherm, dan klapt het vanzelf dicht. Dat is precies het
 * scenario waar de blur voor bestaat, en het is prettiger dan een hard venster dat
 * je midden in een vergelijking afkapt.
 */
const STORAGE_KEY = 'dashboard.amounts-unlocked-until'
const WINDOW_MS = 15 * 60 * 1000
const EXTEND_THROTTLE_MS = 30 * 1000

let expiresAt: number | null = null
let closeTimer: ReturnType<typeof setTimeout> | null = null
let lastExtend = 0

const listeners = new Set<() => void>()

function read(): number | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (raw === null) return null
    const until = Number(raw)
    return Number.isFinite(until) && until > Date.now() ? until : null
  } catch {
    return null
  }
}

function write(until: number | null): void {
  try {
    if (until === null) sessionStorage.removeItem(STORAGE_KEY)
    else sessionStorage.setItem(STORAGE_KEY, String(until))
  } catch {
    // Private mode of een geblokkeerde storage: de ontgrendeling leeft dan alleen
    // in het geheugen. Dat is een prima terugval, geen reden om om te vallen.
  }
}

function emit(): void {
  for (const listener of listeners) listener()
}

function schedule(): void {
  if (closeTimer !== null) clearTimeout(closeTimer)
  closeTimer = expiresAt === null ? null : setTimeout(lockAmounts, Math.max(0, expiresAt - Date.now()))
}

/** Herevalueren na een refresh of na een tab die sliep en zijn timer liet achterlopen. */
function sync(): void {
  const until = read()
  if (until === expiresAt) return
  expiresAt = until
  schedule()
  emit()
}

function extend(): void {
  if (expiresAt === null) return
  const now = Date.now()
  if (now - lastExtend < EXTEND_THROTTLE_MS) return
  lastExtend = now
  expiresAt = now + WINDOW_MS
  write(expiresAt)
  schedule()
}

export function unlockAmounts(): void {
  expiresAt = Date.now() + WINDOW_MS
  lastExtend = Date.now()
  write(expiresAt)
  schedule()
  emit()
}

export function lockAmounts(): void {
  if (expiresAt === null) return
  expiresAt = null
  write(null)
  schedule()
  emit()
}

export function subscribeAmountsUnlock(callback: () => void): () => void {
  listeners.add(callback)
  return () => void listeners.delete(callback)
}

/** Wanneer de ontgrendeling verloopt, of null als er niets openstaat. */
export function amountsUnlockedUntil(): number | null {
  return expiresAt
}

if (typeof window !== 'undefined') {
  expiresAt = read()
  schedule()
  window.addEventListener('pointerdown', extend, { passive: true })
  window.addEventListener('keydown', extend)
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') sync()
  })
}

export interface BlurAmounts {
  /** De opgeslagen voorkeur. */
  enabled: boolean
  /** Wat je nu moet tekenen. */
  blurred: boolean
  unlocked: boolean
  /** Wanneer de ontgrendeling verloopt, voor de tooltip op de knop. */
  expiresAt: number | null
  toggle(): void
}

export function useBlurAmounts(): BlurAmounts {
  const { data } = useAuth()
  const until = useSyncExternalStore(subscribeAmountsUnlock, amountsUnlockedUntil, amountsUnlockedUntil)

  const enabled = data?.preferences.blurAmounts ?? false
  const unlocked = until !== null

  return {
    enabled,
    blurred: enabled && !unlocked,
    unlocked,
    expiresAt: until,
    toggle: () => (unlocked ? lockAmounts() : unlockAmounts()),
  }
}
